import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export type CoachingApproach = 'ontological' | 'agile';

const APPROACH_STORAGE_KEY = 'hupo-coaching-approach';

interface ApproachConfig {
  id: CoachingApproach;
  name: string;
  description: string;
  welcomeTitle: string;
  welcomeSubtitle: string;
  growthAreas: Array<{
    id: string;
    title: string;
    icon: string;
    description: string;
    type: 'main' | 'suggested';
  }>;
  dailyPractices: Array<{
    id: string;
    title: string;
    completed: boolean;
    dueDate: string;
  }>;
  reflectionQuestion: string;
}

const approachConfigs: Record<CoachingApproach, ApproachConfig> = {
  ontological: {
    id: 'ontological',
    name: 'Ontological Coaching',
    description: 'Transform your way of being to unlock new possibilities',
    welcomeTitle: 'Welcome back, Mir! 👋',
    welcomeSubtitle: 'Ready to continue your growth journey?',
    growthAreas: [
      {
        id: 'staying-centered',
        title: 'Staying Centered When Things Get Tense',
        icon: '🧘',
        description:
          'Support yourself to stay steady in the face of discomfort — during conflict, feedback, or moments of stress.',
        type: 'main',
      },
      {
        id: 'breaking-free',
        title: 'Breaking Free from Limiting Beliefs',
        icon: '🔍',
        description:
          'Challenge the inner rules that hold you back — like "I can\'t set boundaries" or "I need permission to take care of myself."',
        type: 'suggested',
      },
      {
        id: 'expressing-emotions',
        title: 'Understanding and Expressing Emotions Constructively',
        icon: '🌱',
        description:
          'Make space for your emotions — not to control them, but to better understand and express them with care.',
        type: 'suggested',
      },
    ],
    dailyPractices: [
      {
        id: 'body-scan',
        title: 'Take 5 minutes for a body scan before pitch meetings',
        completed: false,
        dueDate: 'May 20, 2025',
      },
      {
        id: 'breathing',
        title: 'Complete the 4-7-8 breathing exercise when you sense your temper rising',
        completed: false,
        dueDate: 'May 20, 2025',
      },
      {
        id: 'affirmation',
        title: 'Practice the affirmation "I can stand my ground with care" before your weekly team alignment syncs',
        completed: true,
        dueDate: 'May 20, 2025',
      },
    ],
    reflectionQuestion: 'When was the last time I felt the need to shrink or stay small? What did I tell myself in that moment?',
  },
  agile: {
    id: 'agile',
    name: 'Agile Coaching Mastery',
    description: 'Transform teams through servant leadership, powerful facilitation, and coaching excellence',
    welcomeTitle: 'Welcome back, Mir! 🚀',
    welcomeSubtitle: 'Ready to unlock your team\'s potential through agile mastery?',
    growthAreas: [
      {
        id: 'scrum-values-mindset',
        title: 'Scrum Values & Mindset',
        icon: '🔥',
        description:
          'Embody and instill the five Scrum values. Build courage, commitment, focus, openness, and respect to create high-performing teams.',
        type: 'main',
      },
      {
        id: 'role-clarity-effectiveness',
        title: 'Role Clarity & Effectiveness',
        icon: '⚡',
        description:
          'Master Product Owner, Scrum Master, and Developer accountabilities. Create clear boundaries and effective collaboration patterns.',
        type: 'suggested',
      },
      {
        id: 'systems-thinking-flow',
        title: 'Systems Thinking & Flow',
        icon: '🎯',
        description:
          'Optimize value streams and remove systemic impediments. Apply lean principles to accelerate delivery and maximize flow.',
        type: 'suggested',
      },
    ],
    dailyPractices: [
      {
        id: 'definition-of-done',
        title: 'Refine the Definition of Done (DoD) with the Scrum team before the next Sprint Planning',
        completed: false,
        dueDate: 'May 20, 2025',
      },
      {
        id: 'scrum-master-rotation',
        title: 'Rotate Scrum Master responsibilities temporarily to build cross-role empathy',
        completed: false,
        dueDate: 'May 20, 2025',
      },
      {
        id: 'stakeholder-sync',
        title: 'Set up a weekly stakeholder sync for clearer backlog prioritization input',
        completed: true,
        dueDate: 'May 20, 2025',
      },
    ],
    reflectionQuestion: 'How does the current Sprint Goal help us learn more about our product?',
  },
};

interface AppApproachContextValue {
  currentApproach: CoachingApproach;
  approachConfig: ApproachConfig;
  setApproach: (approach: CoachingApproach) => void;
}

const AppApproachContext = createContext<AppApproachContextValue | undefined>(undefined);

export const AppApproachProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const approachParam = searchParams.get('approach') as CoachingApproach | null;

  // Initialize from URL param, localStorage, or default
  const [currentApproach, setCurrentApproach] = useState<CoachingApproach>(() => {
    // Priority 1: URL parameter
    if (approachParam && approachParam in approachConfigs) {
      localStorage.setItem(APPROACH_STORAGE_KEY, approachParam);
      return approachParam;
    }

    // Priority 2: Stored preference
    const stored = localStorage.getItem(APPROACH_STORAGE_KEY) as CoachingApproach | null;
    if (stored && stored in approachConfigs) {
      return stored;
    }

    // Priority 3: Default
    return 'ontological';
  });

  // Update when URL param changes
  useEffect(() => {
    if (approachParam && approachParam in approachConfigs) {
      setCurrentApproach(approachParam);
      localStorage.setItem(APPROACH_STORAGE_KEY, approachParam);
    } else if (approachParam === null) {
      // If no param provided, use stored or default
      const stored = localStorage.getItem(APPROACH_STORAGE_KEY) as CoachingApproach | null;
      if (stored && stored in approachConfigs) {
        setCurrentApproach(stored);
      }
    }
  }, [approachParam]);

  // Update localStorage when approach changes
  const handleSetApproach = (approach: CoachingApproach) => {
    setCurrentApproach(approach);
    localStorage.setItem(APPROACH_STORAGE_KEY, approach);
  };

  const value: AppApproachContextValue = {
    currentApproach,
    approachConfig: approachConfigs[currentApproach],
    setApproach: handleSetApproach,
  };

  return <AppApproachContext.Provider value={value}>{children}</AppApproachContext.Provider>;
};

export const useAppApproach = () => {
  const context = useContext(AppApproachContext);
  if (!context) {
    throw new Error('useAppApproach must be used within AppApproachProvider');
  }
  return context;
};